import { useEffect, useState } from "react";
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";

const VolunteerDashboard = () => {
  const [pendingDonations, setPendingDonations] = useState([]);
  const [txnIds, setTxnIds] = useState({});
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const name = localStorage.getItem("name");

  // Safety: block direct access
  useEffect(() => {
    if (!token || localStorage.getItem("role") !== "VOLUNTEER") {
      navigate("/login");
    }
  }, [token, navigate]);

  const fetchPending = async () => {
    try {
      const res = await api.get(
        "/donations/pending",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingDonations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleApprove = async (donationId) => {
    if (!txnIds[donationId]) return alert("Enter transaction ID");

    try {
      await api.post(
        `/verify/approve/${donationId}`,
        { transaction_id: txnIds[donationId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setToast("✅ Donation approved successfully!");
      setTimeout(() => setToast(""), 4000);

      setTxnIds((prev) => ({ ...prev, [donationId]: "" }));
      fetchPending();
    } catch (err) {
      console.error(err);
      alert("Error approving donation");
    }
  };

  // Logout
  const logout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  const filteredDonations = pendingDonations
  .filter((d) => {
    const donorName = d?.donor?.name?.toLowerCase() || "";
    const donorPhone = d?.donor?.phone || d?.donor?.mobile || "";
    return (
      donorName.includes(search.toLowerCase()) ||
      donorPhone.includes(search)
    );
  })
  .sort((a, b) => {
    // 1️⃣ Sort by status: IN_PROCESS first, then PENDING
    const statusOrder = { "IN_PROCESS": 0, "PENDING": 1 };
    const statusA = statusOrder[a.status] ?? 2; // any other status goes last
    const statusB = statusOrder[b.status] ?? 2;
    if (statusA !== statusB) return statusA - statusB;

    // 2️⃣ Then sort by date: newest first
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
    
  const handleInProcess = async (donationId) => {
    try {
      await api.patch(
        `/verify/inprocess/${donationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setToast("Donation marked as In Process");
      setTimeout(() => setToast(""), 4000);

      fetchPending();
    } catch (err) {
      console.error(err);
      alert("Error updating status");
    }
  };

  const handleReject = async (donationId) => {
    try {
      if (!window.confirm("Are you sure you want to reject this donation?")) return;
      await api.post(
        `/verify/reject/${donationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setToast("❌ Donation rejected successfully!");
      setTimeout(() => setToast(""), 4000);

      fetchPending();
    } catch (err) {
      console.error(err);
      alert("Error rejecting donation");
    }
  };

 

  return (
    <div className="p-6 min-h-screen bg-rose-50">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-1 text-rose-700">
            Volunteer Dashboard
          </h1>
          <p className="text-gray-500 text-sm">Welcome, {name || "Volunteer"}</p>
        </div>

        <button
          onClick={logout}
          className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg shadow-sm hover:shadow-md transition"
        >
          Logout
        </button>
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
        <input
          type="text"
          placeholder="Search by name or mobile"
          className="border p-3 rounded-lg w-full md:w-1/3 focus:ring-2 focus:ring-rose-400 outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

      </div>

      {/* DONATIONS TABLE */}
      {filteredDonations.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No pending donations</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl shadow-lg">
          <table className="min-w-full table-auto">
            <thead className="bg-rose-100">
              <tr>
                <th className="px-4 py-2 text-left">Donor Name</th>
                <th className="px-4 py-2 text-left">Mobile</th>
                <th className="px-4 py-2 text-left">Amount</th>
	        <th className="px-4 py-2 text-left">Date & Time</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Transaction ID</th>
                <th className="px-4 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDonations.map((d) => (
                <tr key={d._id} className="border-b hover:bg-rose-50">
                  <td className="px-4 py-2">{d?.donor?.name || "N/A"}</td>
                  <td className="px-4 py-2">{d?.donor?.phone || "N/A"}</td>
                  <td className="px-4 py-2 font-semibold text-rose-700">₹{d.amount}</td>

                  {/* Date & Time */}
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {new Date(d.createdAt).toLocaleString()}
                  </td>

                  {/* Status Column */}
                  <td className="px-4 py-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${d.status === "PENDING"
                        ? "bg-yellow-100 text-yellow-700"
                        : d.status === "IN_PROCESS"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                        }`}
                    >
                      {d.status}
                    </span>
                  </td>

                  {/* Transaction ID Input */}
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      placeholder="Enter TXN ID"
                      value={txnIds[d._id] || ""}
                      onChange={(e) =>
                        setTxnIds({ ...txnIds, [d._id]: e.target.value })
                      }
                      className="border p-2 rounded w-full focus:ring-2 focus:ring-rose-400 outline-none"
                    />
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-2 flex gap-2">

                    {d.status === "PENDING" && (
                      <>
                        <button
                          onClick={() => handleInProcess(d._id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-sm"
                        >
                          In Process
                        </button>

                        <button
                          onClick={() => handleReject(d._id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-sm"
                        >
                          Reject
                        </button>
                      </>
                    )}

                    {d.status === "IN_PROCESS" && (
                      <>
                        <button
                          onClick={() => handleApprove(d._id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm"
                        >
                          Approve
                        </button>

                        <button
                          onClick={() => handleReject(d._id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-sm"
                        >
                          Reject
                        </button>
                      </>
                    )}

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg animate-fadeInOut">
          {toast}
        </div>
      )}

      <style>
        {`
          @keyframes fadeInOut {
            0% {opacity: 0; transform: translateY(20px);}
            10% {opacity: 1; transform: translateY(0);}
            90% {opacity: 1; transform: translateY(0);}
            100% {opacity: 0; transform: translateY(20px);}
          }
          .animate-fadeInOut { animation: fadeInOut 4s ease forwards; }
        `}
      </style>
    </div>
  );
};

export default VolunteerDashboard;
